import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { defaultMergePatch } from "./defaultMergePatch";

import type {
  AutosaveStatus,
  UseOptimisticAutosaveOptions,
  UseOptimisticAutosaveResult,
} from "./types";

const DEFAULT_DEBOUNCE_MS = 1000;
const DEFAULT_MAX_DEBOUNCE_MS = 10000;

/**
 * Debounced, optimistic, RQ-cache-managed autosave.
 *
 * The caller pushes sparse diffs via `queueChange`; the hook applies each
 * diff to the React Query cache for the configured `queryKey`, accumulates
 * the diffs into a pending body, and flushes the pending body to `onSave`
 * after a debounce window.
 *
 * On save success, the diff is "promoted" into the rollback snapshot.
 * On save failure, the cache is reverted to the snapshot, undoing every
 * unconfirmed edit. Confirmed edits are never lost on a subsequent failure.
 *
 * For symmetric APIs (cache shape == body shape), specify a single generic.
 * For asymmetric APIs (e.g., HeyApi-generated GET response vs. PATCH request
 * types), pass `TCacheState` as the third generic.
 *
 * @example
 * useOptimisticAutosave<PatchFinancialsTableRequest, void, FinancialsTableResponse>({
 *   queryKey: getFinancialsTableQueryKey({ path: { id: rfpId } }),
 *   onSave: (body) => patchFinancialsTable({ path: { id: rfpId }, body }),
 *   isSameElement: (a, b, path) =>
 *     (path === "coverages" || path === "carriers") &&
 *     isObjectWithId(a) && isObjectWithId(b) && a.id === b.id,
 * });
 */
export function useOptimisticAutosave<
  TBody,
  TResponse = void,
  TCacheState = TBody,
>(
  options: UseOptimisticAutosaveOptions<TBody, TResponse, TCacheState>,
): UseOptimisticAutosaveResult<TBody> {
  const {
    queryKey,
    onSave,
    applyDiff,
    mergeChanges,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    maxDebounceMs = DEFAULT_MAX_DEBOUNCE_MS,
  } = options;

  const queryClient = useQueryClient();

  const [status, setStatus] = useState<AutosaveStatus<TBody>>({
    status: "idle",
  });

  // Refs that track the latest values of things consumed from outside React's
  // synchronous render cycle (timer callbacks, promise handlers). Async paths
  // can rely on these because by the time async code runs, all prior renders
  // have committed and the effect below has fired.
  const statusRef = useRef(status);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const queryKeyRef = useRef(queryKey);
  queryKeyRef.current = queryKey;

  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  // Resolve the merge implementations once per render so callers can pass
  // an override or rely on the default RFC 7396 merge.
  const applyDiffFn: (state: TCacheState, diff: TBody) => TCacheState =
    applyDiff ??
    ((state, diff) => defaultMergePatch(state, diff) as TCacheState);

  const mergeChangesFn: (
    existing: TBody | undefined,
    incoming: TBody,
  ) => TBody =
    mergeChanges ??
    ((existing, incoming) => defaultMergePatch(existing, incoming) as TBody);

  const applyDiffRef = useRef(applyDiffFn);
  applyDiffRef.current = applyDiffFn;

  const mergeChangesRef = useRef(mergeChangesFn);
  mergeChangesRef.current = mergeChangesFn;

  const debounceMsRef = useRef(debounceMs);
  debounceMsRef.current = debounceMs;

  const maxDebounceMsRef = useRef(maxDebounceMs);
  maxDebounceMsRef.current = maxDebounceMs;

  // Imperative bookkeeping that doesn't belong in React state.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firstEditAtRef = useRef<number | null>(null);
  const rollbackBaseRef = useRef<TCacheState | undefined>(undefined);
  const inflightPromiseRef = useRef<Promise<void> | null>(null);
  const destroyedRef = useRef(false);

  const clearTimer = (): void => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Held in refs so the latest closures are always invoked from timers and
  // recursive flushNow calls regardless of which render scheduled them.
  const fireFlushRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const scheduleDebounceRef = useRef<(ms: number) => void>(() => undefined);

  scheduleDebounceRef.current = (ms: number): void => {
    clearTimer();
    timerRef.current = setTimeout(() => {
      void fireFlushRef.current();
    }, ms);
  };

  fireFlushRef.current = async () => {
    if (destroyedRef.current) return;
    clearTimer();

    const current = statusRef.current;
    if (current.status !== "debouncing" && current.status !== "error") {
      return;
    }

    const inflight = current.pending;
    setStatus({ status: "saving", inflight });

    const savePromise = runSave(inflight);
    inflightPromiseRef.current = savePromise;
    try {
      await savePromise;
    } finally {
      if (inflightPromiseRef.current === savePromise) {
        inflightPromiseRef.current = null;
      }
    }
  };

  const runSave = async (inflight: TBody): Promise<void> => {
    try {
      await onSaveRef.current(inflight);
      if (destroyedRef.current) return;
      handleSaveSuccess(inflight);
    } catch (error) {
      if (destroyedRef.current) return;
      handleSaveFailure(inflight, error);
    }
  };

  const handleSaveSuccess = (inflight: TBody): void => {
    // Promote inflight into rollback snapshot — confirmed work is now safe.
    if (rollbackBaseRef.current !== undefined) {
      rollbackBaseRef.current = applyDiffRef.current(
        rollbackBaseRef.current,
        inflight,
      );
    }

    setStatus((prev) => {
      if (prev.status === "saving" && prev.nextPending !== undefined) {
        if (firstEditAtRef.current === null) {
          firstEditAtRef.current = Date.now();
        }
        scheduleDebounceRef.current(debounceMsRef.current);
        return { status: "debouncing", pending: prev.nextPending };
      }
      rollbackBaseRef.current = undefined;
      firstEditAtRef.current = null;
      return { status: "idle" };
    });
  };

  const handleSaveFailure = (inflight: TBody, error: unknown): void => {
    const errInstance =
      error instanceof Error ? error : new Error(String(error));

    // Revert cache to last-known-good. Loses the unconfirmed work in the UI;
    // pending in `error` state preserves it so the caller can retry.
    if (rollbackBaseRef.current !== undefined) {
      queryClient.setQueryData(queryKeyRef.current, rollbackBaseRef.current);
    }

    setStatus((prev) => {
      const pending: TBody =
        prev.status === "saving" && prev.nextPending !== undefined
          ? mergeChangesRef.current(inflight, prev.nextPending)
          : inflight;
      return { status: "error", lastError: errInstance, pending };
    });
    firstEditAtRef.current = null;
  };

  const queueChange = useCallback(
    (incoming: TBody): void => {
      if (destroyedRef.current) return;

      // Snapshot the cache once per save cycle so we have a known-good
      // baseline to revert to on failure. From `error`, the existing snapshot
      // is still valid (cache is already reverted to it).
      if (rollbackBaseRef.current === undefined) {
        rollbackBaseRef.current = queryClient.getQueryData<TCacheState>(
          queryKeyRef.current,
        );
      }

      // Optimistic cache write.
      queryClient.setQueryData<TCacheState>(queryKeyRef.current, (prev) =>
        prev === undefined ? prev : applyDiffRef.current(prev, incoming),
      );

      // Track first-unconfirmed-edit time for the max-age backstop.
      if (firstEditAtRef.current === null) {
        firstEditAtRef.current = Date.now();
      }

      // Functional update: each call reads the latest committed state, so
      // multiple synchronous queueChanges in the same batch each see the
      // previous one's effect.
      setStatus((prev) => {
        if (prev.status === "saving") {
          return {
            status: "saving",
            inflight: prev.inflight,
            nextPending: mergeChangesRef.current(prev.nextPending, incoming),
          };
        }
        const previousPending =
          prev.status === "debouncing" || prev.status === "error"
            ? prev.pending
            : undefined;
        return {
          status: "debouncing",
          pending: mergeChangesRef.current(previousPending, incoming),
        };
      });

      // Schedule the save. If the pending accumulator has been around past
      // `maxDebounceMs`, schedule with 0ms so we fire on the very next tick
      // (after React commits the setStatus above) instead of resetting the
      // window further. Going through the same setTimeout path ensures
      // statusRef is current when fireFlush reads it.
      // Safe: firstEditAtRef is set above when null, so it's always a number by this point.
      const age = Date.now() - firstEditAtRef.current!;
      const ms = age >= maxDebounceMsRef.current ? 0 : debounceMsRef.current;
      scheduleDebounceRef.current(ms);
    },
    [queryClient],
  );

  const flushNow = useCallback(async (): Promise<void> => {
    if (destroyedRef.current) return;
    const current = statusRef.current;

    if (current.status === "idle") return;

    if (current.status === "saving") {
      // Wait for the in-flight save to settle, then recurse: it may have
      // transitioned to `debouncing` (nextPending exists) or `error`.
      await inflightPromiseRef.current;
      await flushNow();
      return;
    }

    // debouncing | error
    clearTimer();
    await fireFlushRef.current();
  }, []);

  // Cancel the debounce timer on unmount and best-effort flush any pending
  // edits so the user doesn't lose the last debounce-window of work on
  // navigation. Fire-and-forget: state updates after unmount are skipped
  // via destroyedRef. Errors are swallowed (the component is gone, there
  // is no UI to surface them on, and the rollback snapshot lives with
  // the component anyway).
  useEffect(
    () => () => {
      destroyedRef.current = true;
      clearTimer();

      const current = statusRef.current;
      const fireSave = onSaveRef.current;

      if (current.status === "debouncing") {
        void fireSave(current.pending).catch(() => undefined);
        return;
      }

      if (current.status === "saving" && current.nextPending !== undefined) {
        // Chain off the in-flight promise so we don't race the server with
        // two concurrent PATCHes that could land out of order.
        const inflight = inflightPromiseRef.current;
        const nextPending = current.nextPending;
        if (inflight !== null) {
          void inflight.then(() =>
            fireSave(nextPending).catch(() => undefined),
          );
        }
      }
      // idle | saving-without-nextPending | error: nothing to do.
    },
    [],
  );

  return { status, queueChange, flushNow };
}
